const latinGroups = [
    ['a', 'á'],
    ['e', 'é'],
    ['i', 'í'],
    ['o', 'ó', 'ö', 'ő'],
    ['u', 'ú', 'ü', 'ű']
]

function toAccentInsensitiveRegex(str) {
    if (!str || typeof str !== 'string') return str
    const s = str.toLowerCase()
    return s
        .split(' ')
        .filter(Boolean)
        .map(word =>
            word.split('').reduce((acc, ch) => {
                const group = latinGroups.find(g => g.includes(ch))
                if (group) acc += `[${group.join('')}]`
                else acc += ch.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
                return acc
            }, '')
        )
        .join('|')
}

const isPlainObject = (o) =>
    o && typeof o === 'object' && !Array.isArray(o) && !(o instanceof Date) && !(o instanceof RegExp)

function deepAssignMissing(target, source) {
    if (!isPlainObject(source)) return target
    for (const k of Object.keys(source)) {
        const sv = source[k]
        const tv = target[k]
        if (tv === undefined) {
            target[k] = sv
        } else if (isPlainObject(sv) && isPlainObject(tv)) {
            deepAssignMissing(tv, sv)
        }
    }
    return target
}

function toArray(val) {
    if (val == null) return []
    const arr = Array.isArray(val) ? val : String(val).split(',')
    return arr.map(s => String(s).trim()).filter(Boolean)
}

function coerceOne(v, t) {
    switch ((t || 'String').toLowerCase()) {
        case 'string':
        case 'regexasstring':
        case 'mongoid':
            return String(v)
        case 'float':
        case 'number':
            return v === '' ? null : Number(v)
        case 'int':
        case 'integer':
            return v === '' ? null : parseInt(v, 10)
        case 'boolean':
            return v === true || String(v).toLowerCase() === 'true' || String(v) === '1'
        case 'date': {
            const d = new Date(v)
            return isNaN(d.getTime()) ? null : d
        }
        default:
            return v
    }
}

function coerce(val, schemaType, isMultiple) {
    return isMultiple ? toArray(val).map(v => coerceOne(v, schemaType)) : coerceOne(val, schemaType)
}

const REGEX_LIKE = /^\/(.+)\/([gimsuy]*)$/

function pruneNulls(obj) {
    if (!isPlainObject(obj)) return obj
    for (const key of Object.keys(obj)) {
        const val = obj[key]
        if (val === null) {
            delete obj[key]
            continue
        }
        if (isPlainObject(val)) {
            pruneNulls(val)
            if (Object.keys(val).length === 0) {
                delete obj[key]
            }
        }
        if (Array.isArray(val)) {
            obj[key] = val.filter(v => v !== null && v !== undefined)
            if (obj[key].length === 0) {
                delete obj[key]
            }
        }
    }
    return obj
}

function endOfDay(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) return date
    const d = new Date(date)
    d.setHours(23, 59, 59, 999)
    return d
}

function buildFilterArgs(schema, query, existingArgs = {}) {
    const result = { filter: {} }

    for (const [field, raw] of Object.entries(query || {})) {
        if (field === '_operators' || field === 'AND' || field === 'OR') continue

        const schemaKey = `filter.${field}`
        const def = schema[schemaKey]
        if (!def) continue

        result.filter[field] = coerce(raw, def.schemaType, !!def.multiple)
    }

    if (isPlainObject(query?._operators)) {
        result.filter._operators = {}
        for (const [field, opsObj] of Object.entries(query._operators)) {
            if (!isPlainObject(opsObj)) continue

            const usableOps = Object.entries(opsObj).filter(([op]) => {
                return !!schema[`filter._operators.${field}.${op}`]
            })
            if (usableOps.length === 0) continue

            for (const [op, raw] of usableOps) {
                const def = schema[`filter._operators.${field}.${op}`] || schema[`filter.${field}`]
                if (!def) continue

                let value

                if (op === 'in' || op === 'nin') {
                    value = coerce(raw, def.schemaType, true)
                } else if (op === 'exists') {
                    value = coerce(raw, 'Boolean', false)
                } else if (op === 'regex') {
                    if (typeof raw === 'string') {
                        const m = raw.match(REGEX_LIKE)
                        if (m) {
                            value = raw
                        } else {
                            const inner = toAccentInsensitiveRegex(raw)
                            value = `/(^|[\\s"])(${inner})(?![^\\s"])/i`
                        }
                    } else {
                        const inner = toAccentInsensitiveRegex(String(raw))
                        value = `/(^|[\\s"])(${inner})(?![^\\s"])/i`
                    }
                } else {
                    value = coerce(raw, def.schemaType, !!def.multiple)

                    if ((op === 'lt' || op === 'lte') && String(def.schemaType).toLowerCase() === 'date') {
                        if (Array.isArray(value)) {
                            value = value.map(v => endOfDay(v))
                        } else {
                            value = endOfDay(value)
                        }
                    }
                }

                if (!result.filter._operators[field]) {
                    result.filter._operators[field] = {}
                }

                result.filter._operators[field][op] = value
            }
        }

        if (Object.keys(result.filter._operators).length === 0) {
            delete result.filter._operators
        }
    }

    // 3) logikai csoportok (opcionális – ha majd kell)
    // if (Array.isArray(query.AND)) result.filter.$and = query.AND.map(...)
    // if (Array.isArray(query.OR))  result.filter.$or  = query.OR.map(...)

    const merged = JSON.parse(JSON.stringify(existingArgs || {}))
    if (!merged.filter) merged.filter = {}
    deepAssignMissing(merged, result)

    pruneNulls(merged)

    return merged
}

export function encodeFilterParam(obj) {
    const json = JSON.stringify(obj)

    const bytes = new TextEncoder().encode(json)

    let b64
    if (typeof Buffer !== 'undefined') {
        b64 = Buffer.from(bytes).toString('base64')
    } else {
        let binary = ''
        bytes.forEach(b => binary += String.fromCharCode(b))
        b64 = btoa(binary)
    }

    b64 = b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    return b64.slice(3) + b64.slice(0, 3)
}

export function decodeFilterParam(str) {
    try {
        const rev = str.slice(-3) + str.slice(0, -3)
        let b64 = rev.replace(/-/g, '+').replace(/_/g, '/')
        while (b64.length % 4) b64 += '='

        let bytes
        if (typeof Buffer !== 'undefined') {
            bytes = Buffer.from(b64, 'base64')
        } else {
            const binary = atob(b64)
            bytes = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i)
            }
        }

        const json = new TextDecoder().decode(bytes)
        return JSON.parse(json)
    } catch {
        return {}
    }
}

export default function initTransformRequests({ wapp }) {
    const middleware = wapp.requests

    if (!Object.prototype.hasOwnProperty.call(middleware, '_defaultSend')) {
        Object.defineProperty(middleware, '_transformRequestParams', {
            value: Object.create(null),
            writable: false,
            enumerable: false,
            configurable: false
        })

        Object.defineProperty(middleware, '_defaultSend', {
            value: middleware.send,
            writable: false,
            enumerable: false,
            configurable: false
        })

        middleware.send = async function(p) {
            const { requestName } = p || {}

            const q = p.req?.query || {}
            let query = {}

            if (q.filter) {
                query = decodeFilterParam(q.filter)
            }

            if (query[requestName]) {

                const schema = p.res?.wappResponse?.store?.getState?.(
                    'res.graphql.query.' + requestName + '.formData'
                )

                if (schema) {

                    const existingArgs = { ...p.args }

                    const finalArgs = buildFilterArgs(schema, query[requestName], existingArgs)

                    p = {
                        ...p,
                        args: finalArgs
                    }

                }

            }

            if (p.res && p.args && requestName.match('FindMany') && p.res.wappResponse.store) {
                p.res.wappResponse.store.dispatch(wapp.states.stateManager.actions.res({
                    type: 'INS_RES',
                    name: 'args',
                    value: {
                        [requestName]: p.args
                    }
                }))
            }

            return await this._defaultSend.call(this, p)
        }
    }
}
